import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Grid,
  Paper,
  Chip,
  InputAdornment
} from '@mui/material';
import {
  Undo as RefundIcon,
  MonetizationOn as MoneyIcon,
  Warning as WarningIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { MaintenanceRecord } from '../../utils/types';
import { formatCurrency } from '../../utils/numberUtils';

interface MaintenanceRefundDialogProps {
  open: boolean;
  onClose: () => void;
  maintenance: MaintenanceRecord | null;
  onRefund: (refundData: RefundData) => Promise<void>;
}

export interface RefundData {
  maintenanceId: string;
  refundAmount: number;
  refundReason: string;
  refundMethod: 'cash' | 'debt_reduction';
  notes?: string;
  employeeId: string;
  employeeName: string;
}

const MaintenanceRefundDialog: React.FC<MaintenanceRefundDialogProps> = ({
  open,
  onClose,
  maintenance,
  onRefund
}) => {
  const [refundAmount, setRefundAmount] = useState<number>(0);
  const [refundReason, setRefundReason] = useState<string>('');
  const [refundMethod, setRefundMethod] = useState<'cash' | 'debt_reduction'>('cash');
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const maxRefundAmount = maintenance?.totalPaid || 0;

  const handleSubmit = async () => {
    if (!maintenance || refundAmount <= 0 || refundAmount > maxRefundAmount) {
      return;
    }

    try {
      setLoading(true);
      
      const refundData: RefundData = {
        maintenanceId: maintenance.id,
        refundAmount,
        refundReason,
        refundMethod,
        notes,
        employeeId: maintenance.receivedByEmployeeId || '',
        employeeName: maintenance.receivedByEmployeeName || ''
      };

      await onRefund(refundData);
      
      // إعادة تعيين النموذج
      setRefundAmount(0);
      setRefundReason('');
      setRefundMethod('cash');
      setNotes('');
      onClose();
    } catch (error) {
      console.error('Error processing refund:', error);
    } finally {
      setLoading(false);
    }
  };

  const refundReasons = [
    'عدم إمكانية الإصلاح',
    'عطل في قطعة غيار',
    'عدم رضا العميل',
    'خطأ في التشخيص',
    'إلغاء العميل للطلب',
    'أخرى'
  ];

  if (!maintenance) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <RefundIcon color="warning" />
          <Typography variant="h6">إرجاع مبلغ الصيانة</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box py={2}>
          {/* معلومات الطلب */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  رقم الطلب
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {maintenance.id.substring(0, 8)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  العميل
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {maintenance.customerName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  نوع الجهاز
                </Typography>
                <Typography variant="body1">
                  {maintenance.deviceType} - {maintenance.devices?.[0]?.size || 'غير محدد'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  المبلغ المدفوع
                </Typography>
                <Chip
                  label={formatCurrency(maintenance.totalPaid || 0)}
                  color="success"
                  icon={<MoneyIcon />}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* تحذير */}
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              ⚠️ سيتم خصم مبلغ الإرجاع من الدرج النقدي أو تقليل دين العميل حسب الطريقة المختارة
            </Typography>
          </Alert>

          {/* مبلغ الإرجاع */}
          <TextField
            label="مبلغ الإرجاع"
            type="number"
            fullWidth
            value={refundAmount}
            onChange={(e) => setRefundAmount(Math.max(0, Math.min(maxRefundAmount, Number(e.target.value))))}
            error={refundAmount > maxRefundAmount}
            helperText={
              refundAmount > maxRefundAmount 
                ? `المبلغ لا يمكن أن يتجاوز ${formatCurrency(maxRefundAmount)}`
                : `الحد الأقصى: ${formatCurrency(maxRefundAmount)}`
            }
            InputProps={{
              endAdornment: <InputAdornment position="end">ريال</InputAdornment>
            }}
            sx={{ mb: 2 }}
          />

          {/* سبب الإرجاع */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>سبب الإرجاع</InputLabel>
            <Select
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              label="سبب الإرجاع"
            >
              {refundReasons.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {reason}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* طريقة الإرجاع */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>طريقة الإرجاع</InputLabel>
            <Select
              value={refundMethod}
              onChange={(e) => setRefundMethod(e.target.value as 'cash' | 'debt_reduction')}
              label="طريقة الإرجاع"
            >
              <MenuItem value="cash">
                <Box display="flex" alignItems="center" gap={1}>
                  <MoneyIcon fontSize="small" />
                  إرجاع نقدي (خصم من الدرج)
                </Box>
              </MenuItem>
              <MenuItem value="debt_reduction">
                <Box display="flex" alignItems="center" gap={1}>
                  <ReceiptIcon fontSize="small" />
                  تقليل دين العميل
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {/* ملاحظات */}
          <TextField
            label="ملاحظات إضافية"
            multiline
            rows={3}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="أي ملاحظات إضافية حول عملية الإرجاع..."
            sx={{ mb: 2 }}
          />

          {/* ملخص العملية */}
          <Paper sx={{ p: 2, bgcolor: 'info.light' }}>
            <Typography variant="subtitle2" gutterBottom>
              ملخص عملية الإرجاع:
            </Typography>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">المبلغ المرتجع:</Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatCurrency(refundAmount)}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">طريقة الإرجاع:</Typography>
              <Typography variant="body2">
                {refundMethod === 'cash' ? 'إرجاع نقدي' : 'تقليل دين العميل'}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2">التأثير على الدرج:</Typography>
              <Typography variant="body2" color={refundMethod === 'cash' ? 'error' : 'success'}>
                {refundMethod === 'cash' 
                  ? `خصم ${formatCurrency(refundAmount)}` 
                  : 'لا يوجد تأثير'
                }
              </Typography>
            </Box>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          إلغاء
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="warning"
          disabled={loading || refundAmount <= 0 || !refundReason}
          startIcon={<RefundIcon />}
        >
          {loading ? 'جاري المعالجة...' : 'تأكيد الإرجاع'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MaintenanceRefundDialog;
